import { useState, useCallback, useMemo } from "react";
import pluralize from "pluralize";

import { ClickableElement } from "@/components/ClickableElement";
import { CreateWorkflowFromRapidModal } from "@/components/flow/shared/Deploy/CreateWorkflowFromRapidModal";
import { ThresholdTracker } from "@/components/flow/shared/Deploy/ThresholdTracker";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "@/contexts/FlowContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSignInAndCreateWorkflow } from "@/hooks/useSignInAndCreateWorkflow";
import { useFlowState } from "@/hooks/useFlowState";
import { useReviewBatchCount } from "@/hooks/useReviewBatchCount";
import { useDatasetImageCount } from "@/hooks/useDatasetImageCount";
import { useFlushCITrainingJob } from "@/hooks/useFlushCITrainingJob";
import { useImproveModel } from "@/hooks/useImproveModel";
import { useContinuousImprovementEnabled } from "@/hooks/useContinuousImprovementEnabled";
import { getImproveButtonProps } from "@/hooks/improveButtonProps";
import {
    useCurrentWorkflow,
    usePredictionsForFileAndFrameSegment,
    useIsTraining,
    useCurrentRapidData
} from "@/store";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { setCachedInferenceResults } from "@/utils/api/datasets";
import { getNextThreshold } from "@/utils/getNextThreshold";
import { DeploySteps, Flows } from "@/types/flows";
import { RapidDeployModal } from "@/components/flow/shared/Deploy/RapidDeployModal";
import { TooltipContainer } from "@scripts/react/components/Tooltip";

export const IntegrateSideMenu = () => {
    const { task } = useFlow();
    const { track } = useAnalytics();
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const { isAdmin, user, requireLogin } = useAuth();
    const { setStep } = useFlowState();
    const { signInAndCreateWorkflow, loading: signInLoading } = useSignInAndCreateWorkflow();
    const workflow = useCurrentWorkflow();
    const { currentWorkspace: workspace } = useCurrentWorkspace();
    const predictions = usePredictionsForFileAndFrameSegment();
    const { count: reviewBatchCount, newDataReady } = useReviewBatchCount();
    const { loading: improveLoading } = useImproveModel();
    const isContinuousImprovementEnabled = useContinuousImprovementEnabled();
    const isTraining = useIsTraining();
    const currentRapidData = useCurrentRapidData();
    const { count: datasetImageCount, datasetId } = useDatasetImageCount();

    const hasScheduledJob =
        currentRapidData?.lastCIEvaluation?.result === CIEvaluationResult.TRAINING_SCHEDULED;
    const { flush: handleTrainNowClick, isFlushing } = useFlushCITrainingJob(datasetId, {
        force: hasScheduledJob
    });

    const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
    const highlightImproveButton = !isTraining && (reviewBatchCount ?? 0) >= 10;

    const imagesUntilRetrain =
        datasetImageCount !== null ? getNextThreshold(datasetImageCount) - datasetImageCount : null;

    const tooltipMessage = useMemo(() => {
        if (!isContinuousImprovementEnabled || imagesUntilRetrain === null) return "";
        if (isTraining)
            return "Your model is already retraining. New data will be used in the next cycle.";
        if (newDataReady) {
            return "New data is ready to improve your model.";
        }
        return `Upload, review, and approve ${imagesUntilRetrain} more ${pluralize("image", imagesUntilRetrain)} to trigger the next retrain.`;
    }, [isContinuousImprovementEnabled, isTraining, newDataReady, imagesUntilRetrain]);

    const handleIntegrateClick = useCallback(() => {
        setIsDeployModalOpen(true);
        track({ event: AnalyticsFlowEvent.DATA_MODE_SELECTED, data: { task, dataMode: "hosted" } });
    }, [track, task]);

    const handleImproveClick = useCallback(() => {
        if (workflow && workspace) {
            if (workflow.url && Object.keys(predictions).length > 0) {
                setCachedInferenceResults(workflow.url, { predictions, annotations: {} });
            }

            setStep({
                flow: Flows.DEPLOY,
                step: DeploySteps.IMPROVE,
                workspaceUrl: workspace.url,
                workflowUrl: workflow.url
            });
        } else {
            signInAndCreateWorkflow();
        }
    }, [workflow, workspace, predictions, setStep, signInAndCreateWorkflow]);

    const isImproveDisabled = improveLoading || (signInLoading && !workflow);
    const baseImproveProps = useMemo(
        () =>
            getImproveButtonProps(isTraining, improveLoading, {
                willPublish: false,
                iconStyle: "fad"
            }),
        [isTraining, improveLoading]
    );

    // When a training job is scheduled and the model isn't already training,
    // replace the entire Improve card with a "Train Now" CTA.
    const showTrainNowCard = hasScheduledJob && !isTraining;
    const improveProps = showTrainNowCard
        ? {
              title: "Train Now",
              description: "New data is ready. Start retraining your model.",
              icon: "fad fa-rotate"
          }
        : baseImproveProps;

    const handleDeployModalClose = useCallback(() => setIsDeployModalOpen(false), []);

    return (
        <div className="content flex flex-col justify-between">
            <div className="flex flex-col gap-4">
                <ClickableElement
                    icon="fad fa-code"
                    title="Deploy Model"
                    description="Use your model on video or images."
                    onClick={handleIntegrateClick}
                    isSelected={false}
                />
                {isAdmin && !isContinuousImprovementEnabled && (
                    <ClickableElement
                        icon="fad fa-sparkles"
                        title="Improve My Model"
                        description="Retrain your model on new data."
                        onClick={handleImproveClick}
                        isSelected={highlightImproveButton}
                        className={highlightImproveButton ? "bg-white hover:bg-tint" : undefined}
                        chip={highlightImproveButton ? "Recommended" : ""}
                        disabled={signInLoading}
                    />
                )}
                {isContinuousImprovementEnabled && (
                    <TooltipContainer
                        type="titleOnly"
                        title={tooltipMessage}
                        placement="right"
                        disabled={!tooltipMessage || showTrainNowCard}
                        customButton={
                            <ClickableElement
                                icon={improveProps.icon}
                                title={improveProps.title}
                                description={improveProps.description}
                                onClick={
                                    showTrainNowCard ? handleTrainNowClick : handleImproveClick
                                }
                                isSelected={showTrainNowCard || newDataReady}
                                className={
                                    showTrainNowCard || newDataReady
                                        ? "bg-white hover:bg-tint"
                                        : undefined
                                }
                                chip={showTrainNowCard ? "" : newDataReady ? "New Data Ready" : ""}
                                disabled={showTrainNowCard ? isFlushing : isImproveDisabled}
                            >
                                {datasetImageCount !== null && !isTraining && !showTrainNowCard && (
                                    <ThresholdTracker
                                        currentImages={datasetImageCount}
                                        highlight={newDataReady}
                                    />
                                )}
                            </ClickableElement>
                        }
                    />
                )}
                <ClickableElement
                    icon="fad fa-network-wired"
                    title="Add Intelligence & Automation"
                    description="Add complex logic to your model with workflows."
                    onClick={() => {
                        track({
                            event: AnalyticsFlowEvent.OPEN_WORKFLOWS_BUTTON_CLICKED,
                            data: {}
                        });
                        if (!user) {
                            requireLogin(() => setIsTemplateModalOpen(true));
                            return;
                        }
                        setIsTemplateModalOpen(true);
                    }}
                    isSelected={false}
                />

                <CreateWorkflowFromRapidModal
                    isOpen={isTemplateModalOpen}
                    onClose={() => setIsTemplateModalOpen(false)}
                />
            </div>

            <RapidDeployModal isOpen={isDeployModalOpen} onClose={handleDeployModalClose} />
        </div>
    );
};
